import { Text, View, ScrollView } from 'react-native'
import { DropdownComponent } from '../../../components/DropdownComponent'
import { ControlServicesContext } from '../../../context/SeaWater/ControlServices/ControlServicesContext'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  CenterCategoryOneType,
  CenterInfo,
  ControlServicesRoutes,
  ShiftType,
} from '../../../navigator/types'
import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native'
import { useForm } from '../../../hooks/useForm'
import { TemplatePage } from '../../../components/TemplatePage'
import { globalStyle } from '../../../theme/global'
import { corporateColor, grayBorders, textColor, toqueColor } from '../../../theme/colors'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/Button'
import { StackNavigationProp } from '@react-navigation/stack'
import { Tag } from '../components'
import { Separator } from '../../../components/Separator'
import { Dimension } from '../components/Dimension'
import { ChoreCard } from '../components/ChoreCard'
import { DeclareFailure } from '../components/DeclareFailure'
import { ShiftSelector } from '../components/ShiftSelector'
import { useValuesFinalForm } from '../hooks/useValuesFinalForm'

interface Navigation extends StackNavigationProp<ControlServicesRoutes, 'JobsAndChore'> {}

type CategoryRoutes = Pick<
  ControlServicesRoutes,
  'CategoryTwo' | 'CategoryThree' | 'CategoryFour' | 'FinalForm'
>

const VIEW_PATHS: { [key: string]: keyof CategoryRoutes } = {
  1: 'FinalForm',
  2: 'CategoryTwo',
  3: 'CategoryThree',
  4: 'CategoryFour',
}

const GO_FINAL_FORM = ['Puerto Cerrado', 'Relevo/Mantención', 'Sin Lavado']
const AM = 'AM'
const PM = 'PM'
const COMPLETE_SHIFT = 'Jornada Completa'
const MAX_JOBS = 5
interface JobListItem {
  [key: string]: {
    jobId: number
    choreId: number
    jobName: string
    choreName: string
    centerId: number
  }
}
export const JobsAndChore = () => {
  const { jobs, chores, modules } = useContext(ControlServicesContext)
  const navigation = useNavigation<Navigation>()
  const route = useRoute<RouteProp<ControlServicesRoutes, 'JobsAndChore'>>()
  const { t } = useTranslation()
  const [jobList, setJobList] = useState<JobListItem>({})
  const [jobListByShift, setJobListByShift] = useState<{ [shift: string]: JobListItem }>({})
  const previousShiftRef = useRef<string | null>(null)

  const {
    values,
    setInitialValues,
    onChangeValues: handleSetValues,
  } = useForm({
    serviceId: route.params?.serviceId ?? 0,
    jobId: route.params?.jobId ?? 0,
    namePage: route.params?.namePage ?? '',
    choreId: route.params?.choreId ?? 0,
    percentage: route.params?.percentage ?? 0,
    centersPercentageTotal: route.params?.centersPercentageTotal ?? 0,
    centersCategoryOne: route.params?.centersCategoryOne ?? ({} as CenterCategoryOneType),
    visibleJobsChores: false,
    shift: AM,
    observation:
      route.params?.observation && route.params?.observation.length > 0
        ? route.params?.observation
        : route.params?.centersCategoryOne[route.params?.currentCenter]?.observation ?? '',
    ceco:
      route.params?.ceco ??
      route.params?.centersCategoryOne[route.params?.currentCenter]?.ceco ??
      'CECO',
  })

  useEffect(() => {
    setInitialValues({
      serviceId: route.params?.serviceId ?? 0,
      jobId: route.params?.jobId ?? 0,
      namePage: route.params?.namePage ?? '',
      choreId: route.params?.choreId ?? 0,
      percentage: route.params?.percentage ?? 0,
      centersCategoryOne: route.params?.centersCategoryOne ?? ({} as CenterCategoryOneType),
      centersPercentageTotal: route.params?.centersPercentageTotal ?? 0,
      visibleJobsChores: false,
      shift: route.params?.shiftState ?? AM,
      observation:
        route.params?.observation && route.params?.observation.length > 0
          ? route.params?.observation
          : route.params?.centersCategoryOne[route.params?.currentCenter]?.observation ?? '',
      ceco:
        route.params?.ceco ??
        route.params?.centersCategoryOne[route.params?.currentCenter]?.ceco ??
        'CECO',
    })
  }, [route.params])

  // Restaurar jobList desde centersCategoryOne para el centro actual y shift actual
  useEffect(() => {
    const isMultipleCenter = Object.keys(route.params.centersCategoryOne).length > 1
    const currentCenterData = values.centersCategoryOne?.[route.params?.currentCenter]
    const currentShift = values.shift

    if (isMultipleCenter) {
      // Para múltiples centros, establecer jobListByShift con todos los shifts desde values
      const newJobListByShift = currentCenterData?.jobList ?? {}

      // Solo actualizar si es diferente para evitar loops
      if (JSON.stringify(newJobListByShift) !== JSON.stringify(jobListByShift)) {
        setJobListByShift(newJobListByShift)
      }
    } else {
      // Para un solo centro, establecer jobList con el shift actual
      if (currentCenterData?.jobList && currentCenterData.jobList[currentShift]) {
        setJobList(currentCenterData.jobList[currentShift])
      } else {
        setJobList({})
      }
    }

    // Actualizar el shift anterior
    previousShiftRef.current = currentShift
  }, [route.params?.currentCenter, values.shift])

  useEffect(() => {
    // Actualizar observation y ceco cuando cambia el centro actual
    const currentCenterData = route.params?.centersCategoryOne?.[route.params?.currentCenter]
    if (currentCenterData) {
      handleSetValues({ name: 'observation', value: currentCenterData.observation ?? '' })
      handleSetValues({ name: 'ceco', value: currentCenterData.ceco ?? 'CECO' })
    }
  }, [route.params?.currentCenter])

  const handleNextCenter = () => {
    const centerIds =
      route.params.centersOrder ?? Object.keys(route.params.centersCategoryOne).map(Number)
    const currentIndex = centerIds.findIndex((id) => id === route.params.currentCenter)

    if (currentIndex < centerIds.length - 1) {
      const nextCenterId = centerIds[currentIndex + 1]
      const currentCenterInfo = values.centersCategoryOne[route.params.currentCenter] as CenterInfo
      const existingJobList = currentCenterInfo?.jobList ?? {}

      // Merge jobListByShift with existing jobList to preserve all shifts
      const mergedJobList = {
        ...existingJobList,
        ...jobListByShift,
      }

      const updatedCentersCategoryOne: CenterCategoryOneType = {
        ...values.centersCategoryOne,
        [route.params.currentCenter]: {
          ...currentCenterInfo,
          jobList: mergedJobList,
        },
      }
      navigation.navigate('JobsAndChore', {
        ...route.params,
        centersCategoryOne: updatedCentersCategoryOne,
        currentCenter: nextCenterId!,
        shiftState: AM,
      })
      setJobListByShift(values.centersCategoryOne[nextCenterId!]?.jobList ?? {})
    } else {
      handleNavigate()
    }
  }

  const handleBackCenter = () => {
    const centerIds =
      route.params.centersOrder ?? Object.keys(route.params.centersCategoryOne).map(Number)
    const currentIndex = centerIds.findIndex((id) => id === route.params.currentCenter)

    if (currentIndex > 0) {
      const prevCenterId = centerIds[currentIndex - 1]

      // Guardar los trabajos del centro actual antes de retroceder
      const currentCenterInfo = values.centersCategoryOne[route.params.currentCenter] as CenterInfo
      const existingJobList = currentCenterInfo?.jobList ?? {}

      const mergedJobList = {
        ...existingJobList,
        ...jobListByShift,
      }

      const updatedCentersCategoryOne: CenterCategoryOneType = {
        ...values.centersCategoryOne,
        [route.params.currentCenter]: {
          ...currentCenterInfo,
          jobList: mergedJobList,
        },
      }

      navigation.navigate('JobsAndChore', {
        ...route.params,
        centersCategoryOne: updatedCentersCategoryOne,
        currentCenter: prevCenterId!,
        shiftState: route.params?.shiftState ?? AM,
      })
      setJobListByShift(() => {
        const prevCenterData = updatedCentersCategoryOne[prevCenterId!]
        return prevCenterData?.jobList ?? {}
      })
    }
  }

  const handleShiftChange = (newShift: ShiftType) => {
    handleSetValues({ name: 'shift', value: newShift })
  }

  // Calcular si hay trabajos guardados en cada turno para el centro actual
  const hasJobsInShift = (shift: ShiftType): boolean => {
    const currentCenterData = values.centersCategoryOne[route.params.currentCenter] as CenterInfo
    if (!currentCenterData?.jobList) return false

    const jobsInShift = currentCenterData.jobList[shift]
    return jobsInShift ? Object.keys(jobsInShift).length > 0 : false
  }

  // Guardar jobListByShift en centersCategoryOne automáticamente para múltiples centros
  useEffect(() => {
    const isMultipleCenter = Object.keys(route.params.centersCategoryOne).length > 1

    if (isMultipleCenter) {
      const currentCenterInfo = values.centersCategoryOne[route.params.currentCenter] as CenterInfo
      const existingJobList = currentCenterInfo?.jobList ?? {}

      // Solo actualizar si hay cambios
      const hasChanges = JSON.stringify(existingJobList) !== JSON.stringify(jobListByShift)

      if (hasChanges) {
        const newCentersCategoryOne: CenterCategoryOneType = {
          ...values.centersCategoryOne,
          [route.params.currentCenter]: {
            ...currentCenterInfo,
            jobList: jobListByShift,
          },
        }

        handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })
      }
    }
  }, [jobListByShift])

  const handleNavigate = () => {
    const routeName = VIEW_PATHS[route.params.categoryId]
    const selectedChore = jobs[values.serviceId]?.find((chore) => chore.id === values.jobId)

    const isGoFinalForm = GO_FINAL_FORM.includes(selectedChore?.name ?? '')
    if (routeName) {
      if (route.params.categoryId === 2 && isGoFinalForm) {
        const selectedChore = chores[values.jobId]?.find((chore) => chore.id === values.choreId)
        navigation.navigate('FinalForm', {
          ...route.params,
          ...values,
          netWashingDetail: [{ cageCount: 0, washingTypeId: selectedChore?.id ?? 0, m2: 0 }],
        })
        return
      }

      const centerIds = Object.keys(route.params.centersCategoryOne)
      const isSingleCenter = centerIds.length === 1
      const isAMShift = values.shift === AM
      const isCompleteShift = values.shift === COMPLETE_SHIFT
      const isPMShift = values.shift === PM
      const isComingFromAM = route.params?.shiftState === PM

      if (
        route.params.categoryId === 1 &&
        isSingleCenter &&
        isAMShift &&
        routeName === 'FinalForm'
      ) {
        const currentCenterInfo = values.centersCategoryOne[
          route.params.currentCenter
        ] as CenterInfo
        const existingJobList = currentCenterInfo?.jobList ?? {}

        const newCentersCategoryOne = {
          ...values.centersCategoryOne,
          [route.params.currentCenter]: {
            ...(values.centersCategoryOne[route.params.currentCenter] as CenterInfo),
            shift: AM,
            completedShifts: [
              ...(values.centersCategoryOne[route.params.currentCenter]?.completedShifts ?? []),
              AM,
            ],
            jobId: values.jobId,
            choreId: values.choreId,
            jobList: {
              ...existingJobList,
              AM: jobList,
            },
          } as CenterInfo,
        } as CenterCategoryOneType
        setJobList({})

        navigation.navigate('JobsAndChore', {
          ...route.params,
          centersCategoryOne: newCentersCategoryOne,
          shiftState: PM,
        })
        return
      }

      if (
        route.params.categoryId === 1 &&
        isSingleCenter &&
        isPMShift &&
        isComingFromAM &&
        routeName === 'FinalForm'
      ) {
        const centerKey = route.params.currentCenter
        const currentCenterInfo = values.centersCategoryOne[centerKey] as CenterInfo
        const existingJobList = currentCenterInfo?.jobList ?? {}

        const newCentersCategoryOne: CenterCategoryOneType = {
          ...values.centersCategoryOne,
          [centerKey]: {
            ...currentCenterInfo,
            shift: PM,
            completedShifts: [...(currentCenterInfo?.completedShifts ?? []), PM],
            jobId: values.jobId,
            choreId: values.choreId,
            jobList: {
              ...existingJobList,
              PM: jobList!,
            },
          },
        }

        navigation.navigate(routeName as 'FinalForm', {
          ...route.params,
          ...values,
          centersCategoryOne: newCentersCategoryOne,
          shiftState: 'PM',
        })
        return
      }

      if (
        route.params.categoryId === 1 &&
        isSingleCenter &&
        isCompleteShift &&
        routeName === 'FinalForm'
      ) {
        const currentCenterInfo = values.centersCategoryOne[
          route.params.currentCenter
        ] as CenterInfo
        const newCentersCategoryOne = {
          ...values.centersCategoryOne,
          [route.params.currentCenter]: {
            ...currentCenterInfo,
            shift: COMPLETE_SHIFT,
            completedShifts: [COMPLETE_SHIFT],
            jobId: values.jobId,
            choreId: values.choreId,
            jobList: {
              [COMPLETE_SHIFT]: jobList,
            },
          } as CenterInfo,
        } as CenterCategoryOneType

        navigation.navigate(routeName, {
          ...route.params,
          ...values,
          centersCategoryOne: newCentersCategoryOne,
          shiftState: 'Jornada Completa',
        })
        return
      }

      const totalCenters = Object.keys(route.params.centersCategoryOne).length
      const isMultipleCenter = totalCenters > 1

      if (!isMultipleCenter) {
        let finalCentersCategoryOne = values.centersCategoryOne
        const currentCenterInfo = values.centersCategoryOne[
          route.params.currentCenter
        ] as CenterInfo
        const existingCompletedShifts = currentCenterInfo?.completedShifts ?? []
        const newCompletedShifts = [...existingCompletedShifts]

        if (!newCompletedShifts.includes(values.shift as ShiftType)) {
          newCompletedShifts.push(values.shift as ShiftType)
        }

        const existingJobList = currentCenterInfo?.jobList ?? {}

        finalCentersCategoryOne = {
          ...values.centersCategoryOne,
          [route.params.currentCenter]: {
            ...currentCenterInfo,
            jobId: values.jobId,
            choreId: values.choreId,
            shift: values.shift,
            completedShifts: newCompletedShifts,
            jobList: {
              ...existingJobList,
              [values.shift]: jobList,
            },
          } as CenterInfo,
        } as CenterCategoryOneType

        navigation.navigate(routeName, {
          ...route.params,
          ...values,
          centersCategoryOne: finalCentersCategoryOne,
        })
      } else {
        const currentCenterInfo = values.centersCategoryOne[
          route.params.currentCenter
        ] as CenterInfo
        const existingJobList = currentCenterInfo?.jobList ?? {}

        const mergedJobList = {
          ...existingJobList,
          ...jobListByShift,
        }

        const updatedCentersCategoryOne: CenterCategoryOneType = {
          ...values.centersCategoryOne,
          [route.params.currentCenter]: {
            ...currentCenterInfo,
            jobList: mergedJobList,
            completedShifts: validateCompletedShifts(route.params.currentCenter),
          },
        }
        handleSetValues({
          name: 'centersCategoryOne',
          value: updatedCentersCategoryOne,
        })
        handleSetValues({ name: 'visibleJobsChores', value: false })
        handleSetValues({ name: 'jobId', value: 0 })
        handleSetValues({ name: 'choreId', value: 0 })

        navigation.navigate(routeName, {
          ...route.params,
          ...values,
          observation: '',
          ceco: 'CECO',
          centersCategoryOne: updatedCentersCategoryOne,
        })
      }
    }
  }

  const validateCompletedShifts: (centerId: number) => ShiftType[] = (centerId: number) => {
    // Validar shifts solo para el centro específico, no globalmente
    const centerInfo = values.centersCategoryOne[centerId] as CenterInfo
    const shiftsCompleted: ShiftType[] = []

    if (!centerInfo?.jobList) {
      return []
    }

    const jobList = Object.entries(centerInfo.jobList)

    jobList.forEach(([shift, jobs]) => {
      if (Object.keys(jobs).length > 0) {
        shiftsCompleted.push(shift as ShiftType)
      }
    })

    return shiftsCompleted
  }

  const { MAX_CHARACTERS, MIN_CHARACTERS } = useValuesFinalForm({})
  const handleDisabled = (): boolean => {
    const { categoryId, centersCategoryOne } = route.params

    const isMultipleCenter = Object.keys(centersCategoryOne).length > 1

    if (categoryId === 1 && isMultipleCenter) {
      const lengthObservation =
        values.centersCategoryOne[route.params.currentCenter]?.observation.length ?? 0

      return (
        Object.keys(jobListByShift?.[values.shift] ?? {}).length === 0 ||
        lengthObservation < MIN_CHARACTERS ||
        lengthObservation > MAX_CHARACTERS ||
        values.visibleJobsChores
      )
    }

    if (categoryId === 1 && !isMultipleCenter) {
      const lengthObservation =
        values.centersCategoryOne[route.params.currentCenter]?.observation.length ?? 0
      return (
        Object.keys(jobList ?? {}).length === 0 ||
        lengthObservation < MIN_CHARACTERS ||
        lengthObservation > MAX_CHARACTERS
      )
    }

    if (categoryId === 2) {
      return values.jobId === 0
    }

    const requiresChoreId = [3, 4].includes(categoryId)
    if (requiresChoreId) return values.jobId === 0 || values.choreId === 0
    return false
  }

  const [resetDropdown, setResetDropdown] = useState(false)

  useFocusEffect(
    useCallback(() => {
      setResetDropdown(true)
      setTimeout(() => setResetDropdown(false), 100)
    }, []),
  )

  const handleBackFunction: () => void = () => {
    const { centersCategoryOne, currentCenter } = route.params
    const centerIds = route.params.centersOrder ?? Object.keys(centersCategoryOne).map(Number)
    const currentIndex = centerIds.findIndex((id) => id === currentCenter)

    if (currentIndex > 0) {
      return handleBackCenter()
    }

    if (centerIds.length === 1 && currentIndex === 0 && values.shift === PM) {
      // Guardar los trabajos de PM antes de volver a AM
      const currentCenterInfo = values.centersCategoryOne[currentCenter] as CenterInfo
      const existingJobList = currentCenterInfo?.jobList ?? {}

      const newCentersCategoryOne: CenterCategoryOneType = {
        ...values.centersCategoryOne,
        [currentCenter]: {
          ...currentCenterInfo,
          jobList: {
            ...existingJobList,
            PM: jobList,
          },
        },
      }

      navigation.navigate('JobsAndChore', {
        ...route.params,
        centersCategoryOne: newCentersCategoryOne,
        shiftState: AM,
      })
      return
    }

    return navigation.goBack()
  }

  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={'CONTROL SERVICES'}
      backButtonFunction={handleBackFunction}
    >
      <ScrollView style={[globalStyle.viewContainer, { padding: 24 }]}>
        <Text style={{ textAlign: 'center', fontWeight: 'bold', color: corporateColor }}>
          {t('Seleccione un trabajo y faena')}
        </Text>

        <View style={{ marginTop: 25, gap: 16 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Text style={{ fontSize: 20, color: toqueColor, fontWeight: '500' }}>
              Centro:
              {route.params.categoryId === 1
                ? route.params.centersCategoryOne[route.params.currentCenter]?.name
                : route.params.centerName}
            </Text>
            {route.params.categoryId === 1 && (
              <Tag
                label='Fecha'
                name={route.params.date.split('-').reverse().join('/')}
                variant='outlined'
              />
            )}
          </View>

          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {route.params.categoryId !== 1 && (
              <Tag
                label='Fecha'
                name={route.params.date.split('-').reverse().join('/')}
                variant='outlined'
              />
            )}
            {route.params.categoryId === 1 && (
              <>
                <Tag label='Embarcación' name={route.params.boardingName} />
                <Tag label='Horario' name={values.shift} variant='outlined' />
              </>
            )}
          </View>
          {route.params.categoryId === 4 && (
            <Dimension modules={modules[route.params.centerId] ?? []} />
          )}
          <Separator />

          {route.params.categoryId === 1 && (
            <ShiftSelector
              selectedShift={values.shift as ShiftType}
              onSelectShift={handleShiftChange}
              totalCenters={Object.keys(route.params.centersCategoryOne).length}
              hasJobsInAM={hasJobsInShift(AM)}
              hasJobsInPM={hasJobsInShift(PM)}
              hasJobsInCompleteShift={hasJobsInShift(COMPLETE_SHIFT)}
            />
          )}

          {Object.values(route.params.centersCategoryOne).length === 1 &&
            Object.entries(jobList ?? {}).map(([key, item], index) => (
              <ChoreCard
                key={key}
                cardTitle={`Trabajo ${index + 1}`}
                info={{ jobName: item.jobName, choreName: item.choreName }}
                onDelete={() => {
                  const updatedList = { ...jobList }
                  delete updatedList[key]
                  setJobList(updatedList)

                  const currentCenterData = values.centersCategoryOne[
                    route.params.currentCenter
                  ] as CenterInfo
                  const existingJobList = currentCenterData?.jobList ?? {}

                  const newCentersCategoryOne: CenterCategoryOneType = {
                    ...values.centersCategoryOne,
                    [route.params.currentCenter]: {
                      ...currentCenterData,
                      jobList: {
                        ...existingJobList,
                        [values.shift]: updatedList,
                      },
                    },
                  }
                  handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })
                }}
              />
            ))}
          {Object.values(route.params.centersCategoryOne).length > 1 && (
            <Text style={{ fontSize: 16, color: textColor, fontWeight: '500' }}>
              Trabajos guardados en Jornada {values.shift} (
              {Object.values(jobListByShift?.[values.shift] ?? {}).length ?? 0})
            </Text>
          )}
          {Object.values(route.params.centersCategoryOne).length > 1 &&
            Object.entries(jobListByShift?.[values.shift] ?? {}).map(([key, item], index) => (
              <ChoreCard
                key={key}
                cardTitle={`Trabajo ${index + 1}`}
                info={{ jobName: item.jobName, choreName: item.choreName }}
                onDelete={() => {
                  const updatedList = { ...jobListByShift?.[values.shift] }
                  delete updatedList[key]
                  setJobListByShift({
                    ...jobListByShift,
                    [values.shift]: updatedList,
                  })

                  const currentCenterData = values.centersCategoryOne[
                    route.params.currentCenter
                  ] as CenterInfo
                  const existingJobList = currentCenterData?.jobList ?? {}

                  const newCentersCategoryOne: CenterCategoryOneType = {
                    ...values.centersCategoryOne,
                    [route.params.currentCenter]: {
                      ...currentCenterData,
                      jobList: {
                        ...existingJobList,
                        [values.shift]: updatedList,
                      },
                    },
                  }
                  handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })
                }}
              />
            ))}

          {(values.visibleJobsChores || route.params.categoryId !== 1) && (
            <>
              <DropdownComponent
                hasSearch
                data={jobs[values.serviceId] ?? []}
                onChange={(value) => {
                  if (route.params.categoryId === 2) {
                    const firstChore = chores[value]
                    handleSetValues({
                      name: 'choreId',
                      value: firstChore
                        ? typeof firstChore[0] !== 'undefined'
                          ? firstChore[0].id
                          : null
                        : null,
                    })
                  }
                  handleSetValues({ name: 'jobId', value })
                }}
                hasIcon
                label='Trabajos'
                defaultOption={{ id: values.jobId, name: '' }}
                placeholder='Seleccione'
                resetTrigger={resetDropdown}
              />

              {route.params.categoryId !== 2 && (
                <DropdownComponent
                  hasSearch
                  data={chores[values.jobId] ?? []}
                  onChange={(value) => {
                    handleSetValues({ name: 'choreId', value })
                  }}
                  hasIcon
                  label='Faenas'
                  placeholder='Seleccione'
                  resetTrigger={resetDropdown}
                />
              )}
            </>
          )}

          {route.params.categoryId === 1 && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              {!values.visibleJobsChores && (
                <Button
                  label='Agregar trabajo'
                  onPress={() => {
                    if (Object.keys(jobList ?? {}).length >= MAX_JOBS) {
                      return
                    }
                    handleSetValues({ name: 'visibleJobsChores', value: true })
                  }}
                  disabled={
                    values.visibleJobsChores || Object.keys(jobList ?? {}).length >= MAX_JOBS
                  }
                  customStyle={{ marginTop: 20, width: 'auto' }}
                  variant={
                    values.visibleJobsChores || Object.keys(jobList ?? {}).length >= MAX_JOBS
                      ? 'orange'
                      : 'white'
                  }
                />
              )}

              {values.visibleJobsChores && (
                <Button
                  label='Cancelar'
                  onPress={() => {
                    if (Object.keys(jobList ?? {}).length >= MAX_JOBS) {
                      return
                    }
                    handleSetValues({ name: 'visibleJobsChores', value: false })
                    handleSetValues({ name: 'jobId', value: 0 })
                    handleSetValues({ name: 'choreId', value: 0 })
                  }}
                  customStyle={{ marginTop: 20, width: 145 }}
                  variant={'white'}
                />
              )}
              <Button
                label='Guardar trabajo'
                onPress={() => {
                  if (Object.keys(jobList ?? {}).length >= MAX_JOBS) {
                    return
                  }
                  handleSetValues({ name: 'visibleJobsChores', value: false })

                  const totalCenters = Object.keys(route.params.centersCategoryOne).length
                  const isMultipleCenter = totalCenters > 1

                  if (isMultipleCenter) {
                    const updatedJobListByShift = { ...jobListByShift }
                    if (!updatedJobListByShift[values.shift]) {
                      updatedJobListByShift[values.shift] = {}
                    }
                    updatedJobListByShift[values.shift] = {
                      ...updatedJobListByShift[values.shift],
                      [`${route.params.currentCenter}-${values.jobId}-${values.choreId}`]: {
                        centerId: route.params.currentCenter,
                        jobId: values.jobId,
                        choreId: values.choreId,
                        jobName:
                          jobs[values.serviceId]?.find((job) => job.id === values.jobId)?.name ??
                          '',
                        choreName:
                          chores[values.jobId]?.find((chore) => chore.id === values.choreId)
                            ?.name ?? '',
                      },
                    }
                    setJobListByShift(updatedJobListByShift)

                    // Also update centersCategoryOne to persist the changes
                    const currentCenterData = values.centersCategoryOne[
                      route.params.currentCenter
                    ] as CenterInfo
                    const existingJobList = currentCenterData?.jobList ?? {}

                    const newCentersCategoryOne: CenterCategoryOneType = {
                      ...values.centersCategoryOne,
                      [route.params.currentCenter]: {
                        ...currentCenterData,
                        jobList: {
                          ...existingJobList,
                          ...updatedJobListByShift,
                        },
                      },
                    }
                    handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })

                    return
                  }

                  // Crear el nuevo trabajo
                  const newJob = {
                    [`${route.params.currentCenter}-${values.jobId}-${values.choreId}`]: {
                      centerId: route.params.currentCenter,
                      jobId: values.jobId,
                      choreId: values.choreId,
                      jobName:
                        jobs[values.serviceId]?.find((job) => job.id === values.jobId)?.name ?? '',
                      choreName:
                        chores[values.jobId]?.find((chore) => chore.id === values.choreId)?.name ??
                        '',
                    },
                  }

                  // Guardar en jobList local
                  const updatedJobList = { ...jobList, ...newJob }
                  setJobList(updatedJobList)

                  const currentCenterData = values.centersCategoryOne[
                    route.params.currentCenter
                  ] as CenterInfo
                  const existingJobList = currentCenterData?.jobList ?? {}

                  const newCentersCategoryOne: CenterCategoryOneType = {
                    ...values.centersCategoryOne,
                    [route.params.currentCenter]: {
                      ...currentCenterData,
                      jobList: {
                        ...existingJobList,
                        [values.shift]: updatedJobList,
                      },
                    },
                  }
                  handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })
                }}
                disabled={
                  values.visibleJobsChores === false || values.jobId === 0 || values.choreId === 0
                }
                customStyle={{ marginTop: 20, width: 'auto' }}
                variant='orange'
              />
            </View>
          )}

          {route.params.categoryId === 1 && (
            <DeclareFailure
              ceco={values.centersCategoryOne[route.params.currentCenter]?.ceco ?? 'CECO'}
              observation={values.centersCategoryOne[route.params.currentCenter]?.observation ?? ''}
              getValues={(val) => {
                const currentCenterData = values.centersCategoryOne[
                  route.params.currentCenter
                ] as CenterInfo
                const newCentersCategoryOne: CenterCategoryOneType = {
                  ...values.centersCategoryOne,
                  [route.params.currentCenter]: {
                    ...currentCenterData,
                    ceco: val.ceco,
                    observation: val.observation,
                  },
                }
                handleSetValues({ name: 'centersCategoryOne', value: newCentersCategoryOne })
              }}
            />
          )}

          {(() => {
            const totalCenters = Object.keys(route.params.centersCategoryOne).length
            const isMultipleCenter = totalCenters > 1
            const centerIds =
              route.params.centersOrder ?? Object.keys(route.params.centersCategoryOne).map(Number)
            const currentIndex = centerIds.findIndex((id) => id === route.params.currentCenter)
            const canGoBack = currentIndex > 0
            const isLastCenter = currentIndex === totalCenters - 1

            return isMultipleCenter ? (
              <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'space-between' }}>
                <Button
                  label={'Anterior'}
                  onPress={handleBackCenter}
                  customStyle={{ width: 'auto', marginBottom: 50, marginTop: 20, flex: 1 }}
                  disabled={!canGoBack}
                  variant={!canGoBack ? 'orange' : 'white'}
                />
                <Button
                  label={isLastCenter ? 'Continuar' : 'Siguiente Centro'}
                  onPress={isLastCenter ? handleNavigate : handleNextCenter}
                  customStyle={{
                    width: 'auto',
                    marginBottom: 50,
                    marginTop: 20,
                    flex: 1,
                    borderColor: handleDisabled() ? grayBorders : toqueColor,
                  }}
                  disabled={handleDisabled()}
                  variant={isLastCenter ? 'orange' : 'white'}
                  labelStyle={handleDisabled() ? { color: 'white' } : {}}
                />
              </View>
            ) : (
              <Button
                label={'Continuar'}
                onPress={handleNavigate}
                customStyle={{ width: 'auto', marginBottom: 50, marginTop: 20 }}
                disabled={handleDisabled()}
              />
            )
          })()}
        </View>
      </ScrollView>
    </TemplatePage>
  )
}
